import {
  useActiveAddress,
  useConnection,
  usePermissions,
  useStrategy,
} from "@arweave-wallet-kit/react"
import styled from "@emotion/styled"
import {
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Link as MuiLink,
  Popover,
  Stack,
  Tooltip,
  useColorScheme,
} from "@mui/material"
import {
  ChevronDown,
  LogOut01,
  Settings04,
  Sun,
  User03,
  Monitor03,
  Moon01,
} from "@untitled-ui/icons-react"

import { useEffect, useMemo, useState } from "react"
import { Link, useLocation } from "react-router-dom"

import { ActiveOrders } from "./ActiveOrders"
import { BotegaLogo } from "./BotegaLogo"
import { BotegaLogoSmall } from "./BotegaLogoSmall"
import { Activity } from "./activity/Activity"
import { formatAddress } from "../utils/format"

type Category = "swap" | "pools" | "mint" | "rewards"

export default function Nav() {
  const { connected, connect, disconnect } = useConnection()
  const address = useActiveAddress()
  const permissions = usePermissions()
  const strategy = useStrategy()

  useEffect(() => {
    if (strategy !== "arconnect" || !connected) {
      return
    }

    if (
      !permissions.includes("ACCESS_ADDRESS") ||
      !permissions.includes("ACCESS_ALL_ADDRESSES") ||
      !permissions.includes("SIGN_TRANSACTION") ||
      !permissions.includes("ACCESS_TOKENS" as any)
    ) {
      console.log("Permissions not granted", strategy, permissions)
      disconnect()
    }
  }, [strategy, permissions, disconnect, connected])

  useEffect(() => {
    if (strategy !== "webwallet" || !connected) {
      return
    }

    if (
      !permissions.includes("ACCESS_ADDRESS") ||
      !permissions.includes("ACCESS_ALL_ADDRESSES") ||
      !permissions.includes("SIGN_TRANSACTION")
    ) {
      console.log("Permissions not granted")
      disconnect()
    }
  }, [strategy, permissions, disconnect, connected])

  // Menu active tab
  const location = useLocation()
  const category = useMemo<Category | undefined>(
    () =>
      location.pathname.includes("/swap")
        ? "swap"
        : location.pathname.includes("/pools")
          ? "pools"
          : location.pathname.includes("/mint")
            ? "mint"
            : "rewards",
    [location],
  )

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const closePopover = () => {
    setAnchorEl(null)
  }

  // Theme switcher
  const { mode, setMode } = useColorScheme()

  // Function to cycle through the theme modes
  const handleThemeToggle = () => {
    const nextMode = mode === "light" ? "dark" : mode === "dark" ? "system" : "light"
    setMode(nextMode)
  }

  // Determine the icon and tooltip based on the current mode
  const themeIcon = mode === "light" ? <Sun /> : mode === "dark" ? <Moon01 /> : <Monitor03 />

  const themeTooltip =
    mode === "light"
      ? "Switch to dark mode"
      : mode === "dark"
        ? "Switch to system mode"
        : "Switch to light mode"

  return (
    <header>
      <Container
        maxWidth="xl"
        sx={{
          paddingY: "1.3rem",
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          justifyItems: "center",

          "& > a:nth-child(1)": {
            justifySelf: "start",
          },
          "& > div:nth-child(3)": {
            justifySelf: "end",
          },
        }}
      >
        {/* Logo */}
        <Box component={Link} to="/" sx={{ height: "20px" }}>
          <Box sx={{ display: { xs: "none", md: "inherit" } }}>
            <BotegaLogo />
          </Box>
          <Box sx={{ display: { xs: "inherit", md: "none" } }}>
            <BotegaLogoSmall />
          </Box>
        </Box>

        {/* Menu links */}
        <Stack
          direction="row"
          gap={3}
          sx={{
            "& > a": {
              minWidth: 40,
              textAlign: "center",
              transition: "color 0.18s ease-in-out",
            },
            "& > a:hover": {
              color: "var(--mui-palette-accent-main)",
            },
          }}
        >
          <MuiLink
            component={Link}
            underline="none"
            color={category === "swap" ? "text.primary" : "text.secondary"}
            to="/"
          >
            Swap
          </MuiLink>
          <MuiLink
            component={Link}
            underline="none"
            color={category === "pools" ? "text.primary" : "text.secondary"}
            to="/pools"
          >
            Pools
          </MuiLink>
          <MuiLink
            component={Link}
            underline="none"
            color={category === "mint" ? "text.primary" : "text.secondary"}
            to="/mint"
          >
            Mint
          </MuiLink>
          <MuiLink
            component={Link}
            underline="none"
            color={category === "rewards" ? "text.primary" : "text.secondary"}
            to="/rewards"
          >
            Rewards Hub
          </MuiLink>
        </Stack>

        {/* Right side buttons */}
        <Stack direction="row" gap={1} alignItems="center">
          {/* Theme switcher */}
          <Box sx={{ display: { xs: "none", md: "inherit" } }}>
            <Tooltip title={themeTooltip}>
              <IconButton size="small" onClick={handleThemeToggle}>
                {themeIcon}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Connect Wallet button */}
          <Button
            size="small"
            variant="contained"
            sx={{
              borderRadius: 5,
              background: "var(--mui-palette-background-paper)",
              minWidth: "unset",
              paddingX: { xs: 0.75, md: 2 },
              "& .MuiButton-endIcon": {
                display: { xs: "none", md: "inherit" },
              },
            }}
            endIcon={connected && address ? <ChevronDown /> : null}
            onClick={(event) => {
              if (connected) {
                setAnchorEl(event.currentTarget)
              } else {
                connect()
              }
            }}
          >
            <Box sx={{ display: { xs: "none", md: "inherit" } }}>
              {(connected && address && formatAddress(address, 6)) || "Connect Wallet"}
            </Box>
            <Box sx={{ display: { xs: "inherit", md: "none" } }}>
              {(connected && address && <User03 viewBox="0 0 24 24" width={16} height={16} />) ||
                "Connect"}
            </Box>
          </Button>
        </Stack>
      </Container>

      <Popover
        open={Boolean(anchorEl)}
        onClose={closePopover}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPopover-paper": {
            width: "420px",
            padding: 1.5,
          },
        }}
      >
        <DropdownMenu>
          <Link to="/my-account" onClick={closePopover}>
            My Account
            <User03 viewBox="0 0 24 24" />
          </Link>
          <Link to="/config" onClick={closePopover}>
            Config
            <Settings04 viewBox="0 0 24 24" />
          </Link>
          <a
            onClick={() => {
              disconnect()
              closePopover()
            }}
          >
            Disconnect
            <LogOut01 viewBox="0 0 24 24" />
          </a>
          <ActiveOrders />
          <Divider />
          <Activity />
        </DropdownMenu>
      </Popover>
    </header>
  )
}

const DropdownMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  & > a {
    display: flex;
    align-items: center;
    gap: 0.56rem;
    text-decoration: none;
    color: var(--mui-palette-text-primary);
    font-weight: 400;
    cursor: pointer;
    transition: all 0.23s ease-in-out;
    justify-content: space-between;

    &:hover {
      opacity: 0.75;
    }

    svg {
      font-size: 0.9em;
      width: 1rem;
      height: 1rem;
    }
  }
`
